import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, findCurrency } from "@/lib/api";
import { LOANS } from "@/constants/testIds";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function LoansHub() {
  const [industries, setIndustries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [iRes, cRes] = await Promise.all([
          api.get("/industries"),
          api.get("/currencies"),
        ]);
        setIndustries(iRes.data.industries);
        setCurrencies(cRes.data.currencies);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const usd = findCurrency(currencies, "USD");

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading industries…
      </div>
    );
  }

  // Bento sizing for up to 7 tiles:
  //   row 1: 0 large (8) + 1 small (4)
  //   row 2: 2 small (4) + 3 large (8)
  //   row 3: 4 half (6) + 5 half (6)
  //   row 4: 6 full-width (12) — final industry gets a hero-style band
  const bentoSpan = (i, total) => {
    if (i === total - 1 && total % 2 === 1 && total >= 5) return "lg:col-span-12";
    if (i === 4 || i === 5) return "lg:col-span-6";
    return i === 0 || i === 3 ? "lg:col-span-8" : "lg:col-span-4";
  };
  const bentoHeight = (i, total) => {
    if (i === total - 1 && total % 2 === 1 && total >= 5) return "min-h-[300px]";
    if (i === 4 || i === 5) return "min-h-[360px]";
    return i === 0 || i === 3 ? "min-h-[420px]" : "min-h-[340px]";
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="max-w-3xl">
        <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-5">
          Business loans
        </p>
        <h1 className="font-display text-4xl lg:text-6xl leading-none tracking-tighter font-light">
          Choose the loan track built for how your business actually earns.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          {industries.length} industry-specific plans — Construction,
          Manufacturing, Fintech, Commercial, Agribusiness, Engineering and
          Mining. Each with its
          own amount envelope, tenure, interest range and document checklist,
          displayed in USD for reference. Proposals can be generated in any of
          45+ currencies.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {industries.map((ind, i) => (
          <Link
            key={ind.id}
            to={`/loans/${ind.id}`}
            data-testid={LOANS.industryCard(ind.id)}
            className={`group relative overflow-hidden rounded-3xl border border-border ${bentoSpan(
              i, industries.length
            )} ${bentoHeight(i, industries.length)} flex`}
          >
            <img
              src={ind.image_url}
              alt={ind.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />

            <div className="relative flex flex-col justify-end p-6 lg:p-8 text-white w-full">
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.3em] uppercase font-semibold text-white/70">
                  {`0${i + 1}`}
                </span>
                <span className="font-mono text-xs text-white/80">
                  {ind.min_interest_rate}%–{ind.max_interest_rate}% p.a.
                </span>
              </div>

              <h3 className="mt-6 font-display text-3xl lg:text-4xl tracking-tighter font-light">
                {ind.name}
              </h3>
              <p className="mt-2 max-w-lg text-sm text-white/80 leading-relaxed">
                {ind.tagline}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/80 font-mono">
                <span>
                  {fmtMoney(ind.min_amount, usd)} –{" "}
                  {fmtMoney(ind.max_amount, usd)}
                </span>
                <span className="opacity-40">|</span>
                <span>
                  {ind.min_tenure_months}–{ind.max_tenure_months} months
                </span>
              </div>

              <div
                data-testid={LOANS.industryCta(ind.id)}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-secondary transition-colors"
              >
                View plan & apply
                <ArrowUpRight aria-hidden="true" className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
